import React, { useState, useEffect } from 'react';
import {
  X,
  Package,
  Edit,
  Trash2,
  Plus,
  Upload,
  History,
  TrendingUp,
  TrendingDown,
  Settings,
  Camera,
  AlertTriangle,
  Save,
  ChevronLeft,
  ChevronRight,
  RefreshCw
} from 'lucide-react';
import { LocalItem } from '../services/itemService';
import { StockMovementForm } from '../types';
import { useStore } from '../utils/StoreContext';
import { useAuth } from '../utils/AuthContext';
import { MovementService } from '../services/movementService';
import { itemService } from '../services/itemService';
import { imageUploadService, UploadedImage } from '../services/imageUploadService';
import { itemImageService } from '../services/itemImageService';
import LoadingSpinner from './LoadingSpinner';

interface ItemDetailsModalProps {
  item: LocalItem;
  onClose: () => void;
  onItemUpdated: (updatedItem?: LocalItem) => void;
  onItemDeleted: () => void;
}

const ItemDetailsModal: React.FC<ItemDetailsModalProps> = ({
  item: initialItem,
  onClose,
  onItemUpdated,
  onItemDeleted
}) => {
  const { currentStore } = useStore();
  const { user } = useAuth();
  const [item, setItem] = useState<LocalItem>(initialItem);
  const [activeTab, setActiveTab] = useState<'details' | 'images' | 'adjustments' | 'history'>('details');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [deletingImageId, setDeletingImageId] = useState<string | null>(null);
  const [syncingPrice, setSyncingPrice] = useState(false);

  // Adjustment form state
  const [adjustmentForm, setAdjustmentForm] = useState({
    mode: 'quantity', // 'quantity' or 'balance'
    quantity: 0,
    targetBalance: item.current_stock,
    unitCost: item.average_cost,
    unitPrice: item.average_cost * 1.3,
    remarks: ''
  });

  // Update form defaults when item changes
  useEffect(() => {
    setAdjustmentForm(prev => ({
      ...prev,
      targetBalance: item.current_stock,
      unitCost: item.average_cost,
      unitPrice: item.average_cost * 1.3
    }));
  }, [item.current_stock, item.average_cost]);

  // Movement history
  const [movementHistory, setMovementHistory] = useState<any[]>([]);

  // Load movement history when history tab is selected
  useEffect(() => {
    if (activeTab === 'history') {
      loadMovementHistory();
    }
  }, [activeTab]);

  // Refresh item data to get updated stock levels
  const refreshItemData = async () => {
    try {
      const result = await itemService.searchLocalItems(item.sku, currentStore?.id, 1, 1);
      if (result.data && result.data.length > 0) {
        const updatedItem = result.data[0];
        setItem(updatedItem);
        onItemUpdated(updatedItem);
      }
    } catch (err) {
      console.error('Error refreshing item data:', err);
    }
  };

  const loadMovementHistory = async () => {
    setLoading(true);
    try {
      const result = await MovementService.getItemTransactionHistory(
        item.id, 
        currentStore?.id || '', 
        50, 
        0
      );
      
      if (result.error) {
        setError('Failed to load movement history');
        console.error('Movement history error:', result.error);
      } else {
        setMovementHistory(result.data?.transactions || []);
      }
    } catch (err) {
      setError('Failed to load movement history');
      console.error('Movement history error:', err);
    } finally {
      setLoading(false);
    }
  };

  // Calculate adjustment quantity
  const getAdjustmentQuantity = () => {
    return adjustmentForm.quantity;
  };

  const handleAdjustment = async () => {
    if (!currentStore || !user) return;

    const adjustmentQuantity = getAdjustmentQuantity();
    
    if (adjustmentQuantity === 0) {
      setError('No adjustment needed - quantity is 0');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      console.log('=== ADJUSTMENT START ===');
      console.log('Current item:', item);
      console.log('Current store:', currentStore);
      console.log('User:', user);
      console.log('Adjustment form:', adjustmentForm);
      console.log('Calculated quantity:', adjustmentQuantity);

      const movementData: StockMovementForm = {
        movement_type: 'adjustment' as const,
        store_id: currentStore.id,
        movement_date: new Date().toISOString().split('T')[0],
        remarks: adjustmentForm.remarks || 
          `Stock adjustment: ${adjustmentQuantity > 0 ? 'increase' : 'decrease'} by ${Math.abs(adjustmentQuantity)}`,
        details: [{ // Correct: using 'details' as per StockMovementForm interface
          item_id: item.id,
          quantity: adjustmentQuantity,
          unit_price: adjustmentForm.unitPrice,
          unit_cost: adjustmentForm.unitCost,
          order_references: `Adjustment for ${item.name}`
        }]
      };

      console.log('Movement data prepared:', movementData);
      console.log('Calling MovementService.createMovement...');

      const result = await MovementService.createMovement(movementData, user.id);
      
      console.log('MovementService response:', result);
      
      if (result.error) {
        setError(`Failed to create adjustment: ${result.error}`);
      } else {
        // Clear any errors and show success
        setError(null);
        setSuccessMessage(`Adjustment created successfully! Stock updated.`);
        
        // Refresh item data to get updated stock levels
        await refreshItemData();
        
        // Switch to history tab to show the new adjustment
        setActiveTab('history');
        
        // Reset form for next adjustment
        setAdjustmentForm({
          mode: 'quantity',
          quantity: 0,
          targetBalance: item.current_stock,
          unitCost: item.average_cost,
          unitPrice: item.average_cost * 1.3,
          remarks: ''
        });
        
        // Load movement history to show the new adjustment
        await loadMovementHistory();
        
        // Clear success message after 3 seconds
        setTimeout(() => setSuccessMessage(null), 3000);
      }
    } catch (err) {
      console.error('=== ADJUSTMENT FAILED ===');
      console.error('Error details:', err);
      console.error('Error message:', err instanceof Error ? err.message : String(err));
      setError(`Failed to create adjustment: ${err instanceof Error ? err.message : String(err)}`);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteItem = async () => {
    // TODO: Implement item deletion functionality
    // This would require a backend API endpoint to safely remove items
    setError('Item deletion functionality coming soon');
    setShowDeleteConfirm(false);
  };

  const handleSyncPrice = async () => {
    if (!currentStore || !user) return;

    setSyncingPrice(true);
    setError(null);
    setSuccessMessage(null);

    try {
      console.log('Syncing price from POS API for SKU:', item.sku);

      const result = await itemService.updateItemPriceFromPOS(
        item.id,
        item.sku,
        currentStore.id,
        user.id
      );

      if (result.error) {
        setError(`Failed to sync price: ${result.error}`);
      } else if (result.data) {
        setSuccessMessage(
          `Price updated successfully! New price: MVR ${result.data.new_price}`
        );

        // Refresh item data to show updated price
        await refreshItemData();

        // Clear success message after 5 seconds
        setTimeout(() => setSuccessMessage(null), 5000);
      }
    } catch (err) {
      console.error('Error syncing price:', err);
      setError(`Failed to sync price: ${err instanceof Error ? err.message : String(err)}`);
    } finally {
      setSyncingPrice(false);
    }
  };

  const handleImageUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (!user) {
      setError('User authentication required');
      return;
    }

    setUploadingImage(true);
    setError(null);

    try {
      const result = await imageUploadService.uploadImage(file);
      
      if (result.success && result.data) {
        const isPrimary = item.images.length === 0; // First image becomes primary
        
        // Save image to database
        const dbResult = await itemImageService.addItemImage(
          user.id,
          item.id,
          result.data.url,
          isPrimary,
          item.images.length + 1
        );
        
        if (dbResult.success) {
          // Add the new image to the item's images array with database ID
          const newImage = {
            id: dbResult.data.image_id, // Use database-generated UUID
            image_url: result.data.url,
            is_primary: isPrimary,
            image_order: item.images.length + 1
          };
          
          const updatedItem = {
            ...item,
            images: [...item.images, newImage],
            primary_image: isPrimary ? result.data.url : item.primary_image
          };
          
          setItem(updatedItem);
          onItemUpdated(updatedItem);
          setSuccessMessage('Image uploaded and saved successfully!');
          
          // Clear success message after 3 seconds
          setTimeout(() => setSuccessMessage(null), 3000);
        } else {
          // Image uploaded but failed to save to database
          setError(`Image uploaded but failed to save to database: ${dbResult.error}`);
          
          // Try to delete the uploaded image from external service
          try {
            await imageUploadService.deleteImage(result.data.id);
          } catch (cleanupError) {
            console.warn('Failed to cleanup uploaded image:', cleanupError);
          }
        }
      } else {
        setError(result.error || 'Failed to upload image');
      }
    } catch (err) {
      console.error('Image upload error:', err);
      setError('Failed to upload image');
    } finally {
      setUploadingImage(false);
      // Clear the file input
      event.target.value = '';
    }
  };

  const handleSetPrimaryImage = async (imageIndex: number) => {
    if (item.images[imageIndex]?.is_primary) return; // Already primary

    if (!user) {
      setError('User authentication required');
      return;
    }

    setError(null);

    try {
      const imageId = item.images[imageIndex].id;
      
      // Update primary image in database
      const dbResult = await itemImageService.setPrimaryItemImage(user.id, imageId);
      
      if (dbResult.success) {
        // Update the images array to set new primary
        const updatedImages = item.images.map((img, index) => ({
          ...img,
          is_primary: index === imageIndex
        }));
        
        const updatedItem = {
          ...item,
          images: updatedImages,
          primary_image: updatedImages[imageIndex].image_url // Update primary_image field too
        };
        
        setItem(updatedItem);
        onItemUpdated(updatedItem);
        setSuccessMessage('Primary image updated successfully!');
        
        // Clear success message after 3 seconds
        setTimeout(() => setSuccessMessage(null), 3000);
      } else {
        setError(`Failed to set primary image: ${dbResult.error}`);
      }
    } catch (err) {
      console.error('Set primary image error:', err);
      setError('Failed to set primary image');
    }
  };

  const handleImageDelete = async (imageId: string, imageIndex: number) => {
    const imageToDelete = item.images[imageIndex];
    
    // Check if image is from the allowed domain using the service method
    const isFromAllowedDomain = imageUploadService.isFromOurService(imageToDelete.image_url);
    
    // Debug logging
    console.log('🔍 Image Delete Debug:');
    console.log('Image URL:', imageToDelete.image_url);
    console.log('Is from our service?', isFromAllowedDomain);
    console.log('URL starts with https://imguplaod.beautello.mv?', imageToDelete.image_url.startsWith('https://imguplaod.beautello.mv'));
    
    if (!isFromAllowedDomain) {
      setError('Only images uploaded to imguplaod.beautello.mv can be deleted');
      return;
    }

    if (!window.confirm('Are you sure you want to delete this image?')) return;

    if (!user) {
      setError('User authentication required');
      return;
    }

    setDeletingImageId(imageId);
    setError(null);

    try {
      // Delete from database first
      const dbResult = await itemImageService.deleteItemImage(user.id, imageId);
      
      if (dbResult.success) {
        // Delete from external image service (imguplaod.beautello.mv)
        const imageUrl = imageToDelete.image_url;
        const externalImageId = imageUploadService.extractImageId(imageUrl);
        
        if (externalImageId) {
          try {
            const deleteResult = await imageUploadService.deleteImage(externalImageId);
            if (!deleteResult.success) {
              console.warn('Failed to delete from external image service:', deleteResult.error);
              // Continue anyway since database deletion succeeded
            }
          } catch (externalError) {
            console.warn('Failed to delete from external image service:', externalError);
            // Continue anyway since database deletion succeeded
          }
        }
        
        // Remove the image from the item's images array
        const updatedImages = item.images.filter((_, index) => index !== imageIndex);
        const wasDeleted = item.images[imageIndex];
        
        const updatedItem = {
          ...item,
          images: updatedImages,
          // Update primary_image if the deleted image was primary
          primary_image: wasDeleted.is_primary && updatedImages.length > 0 
            ? updatedImages.find(img => img.is_primary)?.image_url || updatedImages[0]?.image_url
            : wasDeleted.is_primary 
              ? undefined 
              : item.primary_image
        };
        
        setItem(updatedItem);
        onItemUpdated(updatedItem);
        
        // Adjust current image index if necessary
        if (currentImageIndex >= updatedImages.length && updatedImages.length > 0) {
          setCurrentImageIndex(updatedImages.length - 1);
        } else if (updatedImages.length === 0) {
          setCurrentImageIndex(0);
        }
        
        setSuccessMessage('Image deleted successfully!');
        
        // Clear success message after 3 seconds
        setTimeout(() => setSuccessMessage(null), 3000);
      } else {
        setError(`Failed to delete image: ${dbResult.error}`);
      }
    } catch (err) {
      console.error('Image delete error:', err);
      setError('Failed to delete image');
    } finally {
      setDeletingImageId(null);
    }
  };

  const nextImage = () => {
    if (item.images.length > 1) {
      setCurrentImageIndex((prev) => (prev + 1) % item.images.length);
    }
  };

  const prevImage = () => {
    if (item.images.length > 1) {
      setCurrentImageIndex((prev) => (prev - 1 + item.images.length) % item.images.length);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-2 sm:p-4">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-sm sm:max-w-lg md:max-w-2xl lg:max-w-4xl max-h-[95vh] overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-4 sm:p-6 border-b border-gray-200 bg-gradient-to-r from-blue-50 to-indigo-50">
          <div className="flex items-center gap-3 min-w-0 flex-1">
            <div className="bg-blue-100 p-2 rounded-lg flex-shrink-0">
              <Package className="w-5 h-5 text-blue-600" />
            </div>
            <div className="min-w-0 flex-1">
              <h3 className="text-lg font-semibold text-gray-900 truncate">{item.name}</h3>
              <div className="flex flex-wrap items-center gap-4 mt-1 text-sm text-gray-600">
                <span>SKU: {item.sku}</span>
                {item.barcode && <span>Barcode: {item.barcode}</span>}
                <span className={`font-semibold px-2 py-1 rounded-full text-xs ${
                  item.current_stock > 10 
                    ? 'bg-green-100 text-green-700' 
                    : item.current_stock > 0
                      ? 'bg-yellow-100 text-yellow-700'
                      : 'bg-red-100 text-red-700'
                }`}>
                  Stock: {item.current_stock}
                </span>
              </div>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-white/50 transition-colors flex-shrink-0 ml-2"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Tabs */}
        <div className="border-b border-gray-200">
          <nav className="flex space-x-4 sm:space-x-8 px-4 sm:px-6 overflow-x-auto">
            {[
              { id: 'details', label: 'Details', icon: Package },
              { id: 'images', label: 'Images', icon: Camera },
              { id: 'adjustments', label: 'Adjustments', icon: Settings },
              { id: 'history', label: 'History', icon: History }
            ].map(({ id, label, icon: Icon }) => (
              <button
                key={id}
                onClick={() => {
                  setActiveTab(id as any);
                  setError(null);
                  setSuccessMessage(null);
                }}
                className={`py-3 sm:py-4 px-2 sm:px-1 border-b-2 font-medium text-xs sm:text-sm flex items-center gap-1 sm:gap-2 transition-colors whitespace-nowrap ${
                  activeTab === id
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <Icon className="w-4 h-4 sm:w-4 sm:h-4" />
                <span className="hidden sm:inline">{label}</span>
                <span className="sm:hidden">{label.charAt(0)}</span>
              </button>
            ))}
          </nav>
        </div>

        {/* Content */}
        <div className="p-4 sm:p-6 overflow-y-auto max-h-[calc(95vh-180px)] sm:max-h-[calc(95vh-200px)]">
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
              <div className="flex items-center gap-2">
                <AlertTriangle className="w-4 h-4 text-red-600" />
                <span className="text-sm text-red-700">{error}</span>
              </div>
            </div>
          )}

          {successMessage && (
            <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-6">
              <div className="flex items-center gap-2">
                <Save className="w-4 h-4 text-green-600" />
                <span className="text-sm text-green-700">{successMessage}</span>
              </div>
            </div>
          )}

          {/* Details Tab */}
          {activeTab === 'details' && (
            <div className="space-y-6">
              {/* Basic Information */}
              <div className="bg-gray-50 rounded-lg p-4">
                <h4 className="font-semibold text-gray-900 mb-4">Basic Information</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Name</label>
                    <div className="text-sm text-gray-900 bg-white px-3 py-2 rounded border">{item.name}</div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">SKU</label>
                    <div className="text-sm text-gray-900 bg-white px-3 py-2 rounded border font-mono">{item.sku}</div>
                  </div>
                  {item.barcode && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Barcode</label>
                      <div className="text-sm text-gray-900 bg-white px-3 py-2 rounded border font-mono">{item.barcode}</div>
                    </div>
                  )}
                  {item.brand && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Brand</label>
                      <div className="text-sm text-gray-900 bg-white px-3 py-2 rounded border">{item.brand}</div>
                    </div>
                  )}
                  {item.category && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Category</label>
                      <div className="text-sm text-gray-900 bg-white px-3 py-2 rounded border">{item.category}</div>
                    </div>
                  )}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                    <div className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                      item.is_active ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                    }`}>
                      {item.is_active ? 'Active' : 'Inactive'}
                    </div>
                  </div>
                </div>
                {item.description && (
                  <div className="mt-4">
                    <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                    <div className="text-sm text-gray-900 bg-white px-3 py-2 rounded border">
                      {item.description}
                    </div>
                  </div>
                )}
              </div>

              {/* Stock Information */}
              <div className="bg-blue-50 rounded-lg p-4">
                <h4 className="font-semibold text-gray-900 mb-4">Stock Information</h4>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Current Stock</label>
                    <div className={`text-2xl font-bold px-4 py-3 rounded-lg text-center ${
                      item.current_stock > 10 
                        ? 'bg-green-100 text-green-700 border border-green-200' 
                        : item.current_stock > 0
                          ? 'bg-yellow-100 text-yellow-700 border border-yellow-200'
                          : 'bg-red-100 text-red-700 border border-red-200'
                    }`}>
                      {item.current_stock}
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Average Cost</label>
                    <div className="text-lg font-medium text-gray-900 bg-white px-3 py-3 rounded border text-center">
                      MVR {item.average_cost.toFixed(2)}
                    </div>
                  </div>
                  {item.latest_unit_price && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Latest Unit Price</label>
                      <div className="text-lg font-medium text-blue-600 bg-blue-50 px-3 py-3 rounded border border-blue-200 text-center">
                        MVR {item.latest_unit_price.toFixed(2)}
                      </div>
                    </div>
                  )}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Total Value</label>
                    <div className="text-lg font-medium text-gray-900 bg-white px-3 py-3 rounded border text-center">
                      MVR {item.total_value.toFixed(2)}
                    </div>
                  </div>
                </div>
              </div>

              {/* Actions */}
              {user?.user_type === 'Admin' && (
                <div className="bg-gray-50 rounded-lg p-4">
                  <h4 className="font-semibold text-gray-900 mb-4">Actions</h4>
                  <div className="flex flex-wrap gap-3">
                    <button
                      onClick={handleSyncPrice}
                      disabled={syncingPrice}
                      className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg flex items-center gap-2 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <RefreshCw className={`w-4 h-4 ${syncingPrice ? 'animate-spin' : ''}`} />
                      {syncingPrice ? 'Syncing...' : 'Sync Price from POS'}
                    </button>
                    <button
                      onClick={() => setActiveTab('adjustments')}
                      className="btn-primary flex items-center gap-2"
                    >
                      <Settings className="w-4 h-4" />
                      Create Adjustment
                    </button>
                    <button
                      onClick={() => setShowDeleteConfirm(true)}
                      className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-lg flex items-center gap-2 transition-colors"
                    >
                      <Trash2 className="w-4 h-4" />
                      Remove Item
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Images Tab */}
          {activeTab === 'images' && (
            <div className="space-y-6">
              {/* Current Images */}
              <div>
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <h4 className="font-semibold text-gray-900">Item Images ({item.images.length})</h4>
                    {user?.user_type === 'Admin' && item.images.length > 0 && (
                      <div>
                        <p className="text-xs text-gray-500 mt-1">
                          Right-click any image or use the settings button to set as primary
                        </p>
                        {/* Debug info - remove in production */}
                        <details className="text-xs text-gray-400 mt-1">
                          <summary>Debug: Image URLs</summary>
                          {item.images.map((img, idx) => (
                            <div key={idx} className="mt-1">
                              <span className="font-mono text-xs">{idx + 1}:</span> 
                              <span className={imageUploadService.normalizeUrl(img.image_url).startsWith('https://imguplaod.beautello.mv') ? 'text-green-600' : 'text-red-600'}>
                                {imageUploadService.normalizeUrl(img.image_url).substring(0, 50)}...
                              </span>
                            </div>
                          ))}
                        </details>
                      </div>
                    )}
                  </div>
                  {user?.user_type === 'Admin' && (
                    <label className={`btn-secondary flex items-center gap-2 cursor-pointer ${
                      uploadingImage ? 'opacity-50 cursor-not-allowed' : ''
                    }`}>
                      {uploadingImage ? (
                        <>
                          <LoadingSpinner />
                          Uploading...
                        </>
                      ) : (
                        <>
                      <Plus className="w-4 h-4" />
                      Add Image
                        </>
                      )}
                      <input
                        type="file"
                        accept="image/*"
                        onChange={handleImageUpload}
                        disabled={uploadingImage}
                        className="hidden"
                      />
                    </label>
                  )}
                </div>

                {item.images.length > 0 ? (
                  <div className="space-y-4">
                    {/* Images List */}
                    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                      {item.images.map((image, index) => (
                        <div 
                          key={`${image.id}-${index}`} 
                          className="relative group bg-gray-100 rounded-lg overflow-hidden aspect-square cursor-pointer"
                          onContextMenu={(e) => {
                            e.preventDefault();
                            if (user?.user_type === 'Admin' && !image.is_primary) {
                              handleSetPrimaryImage(index);
                            }
                          }}
                          title={image.is_primary ? 'Primary image' : 'Right-click to set as primary'}
                        >
                          <img
                            src={imageUploadService.normalizeUrl(image.image_url)}
                            alt={`${item.name} - Image ${index + 1}`}
                            className="w-full h-full object-cover transition-transform group-hover:scale-105"
                          onError={(e) => {
                              (e.target as HTMLImageElement).src = '/images/btlogo.jpg';
                          }}
                        />
                          
                          {/* Primary badge */}
                          {image.is_primary && (
                            <div className="absolute top-2 left-2 bg-blue-500 text-white text-xs px-2 py-1 rounded-full font-medium shadow-md">
                              ⭐ Primary
                      </div>
                          )}
                          
                          {/* Deletable indicator */}
                          {!imageUploadService.isFromOurService(image.image_url) && (
                            <div className="absolute top-2 right-2 bg-gray-500 text-white text-xs px-2 py-1 rounded-full font-medium shadow-md" title={`URL: ${image.image_url}`}>
                              🔒 Protected
                            </div>
                          )}
                          
                          {/* Image actions overlay */}
                          {user?.user_type === 'Admin' && (
                            <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                          <button
                                onClick={() => {
                                  setCurrentImageIndex(index);
                                  // You could add a modal here to view full size
                                }}
                                className="bg-white/20 hover:bg-white/30 text-white p-2 rounded-full transition-colors"
                                title="View full size"
                              >
                                <Camera className="w-4 h-4" />
                          </button>
                              {!image.is_primary && (
                          <button
                                  onClick={() => handleSetPrimaryImage(index)}
                                  className="bg-blue-500/80 hover:bg-blue-600/80 text-white p-2 rounded-full transition-colors"
                                  title="Set as primary image"
                          >
                                  <Settings className="w-4 h-4" />
                          </button>
                              )}
                              {/* Only show delete button for images from allowed domain */}
                              {imageUploadService.isFromOurService(image.image_url) && (
                                <button
                                  onClick={() => handleImageDelete(image.id, index)}
                                  disabled={deletingImageId === image.id}
                                  className="bg-red-500/80 hover:bg-red-600/80 text-white p-2 rounded-full transition-colors disabled:opacity-50"
                                  title={`Delete image (URL: ${image.image_url})`}
                                >
                                  {deletingImageId === image.id ? (
                                    <LoadingSpinner />
                                  ) : (
                                    <Trash2 className="w-4 h-4" />
                                  )}
                                </button>
                              )}
                            </div>
                          )}
                          
                          {/* Image index */}
                        <div className="absolute bottom-2 right-2 bg-black/50 text-white text-xs px-2 py-1 rounded">
                            {index + 1}
                        </div>
                    </div>
                        ))}
                      </div>
                  </div>
                ) : (
                  <div className="text-center py-12 border-2 border-dashed border-gray-300 rounded-lg">
                    <Camera className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                    <h4 className="text-lg font-medium text-gray-900 mb-2">No Images</h4>
                    <p className="text-gray-500 mb-4">This item doesn't have any images yet.</p>
                    {user?.user_type === 'Admin' && (
                      <label className={`btn-primary cursor-pointer inline-flex items-center gap-2 ${
                        uploadingImage ? 'opacity-50 cursor-not-allowed' : ''
                      }`}>
                        {uploadingImage ? (
                          <>
                            <LoadingSpinner />
                            Uploading...
                          </>
                        ) : (
                          <>
                        <Upload className="w-4 h-4" />
                        Upload First Image
                          </>
                        )}
                        <input
                          type="file"
                          accept="image/*"
                          onChange={handleImageUpload}
                          disabled={uploadingImage}
                          className="hidden"
                        />
                      </label>
                    )}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Adjustments Tab */}
          {activeTab === 'adjustments' && (
            <div>
              {user?.user_type === 'Admin' ? (
                <div className="bg-gray-50 rounded-lg p-4 max-w-md mx-auto">
                  <h4 className="font-semibold text-gray-900 mb-4 text-center">Adjustment Preview</h4>
                  
                  {/* Minimal Balance Flow */}
                  <div className="flex items-center justify-center gap-2 mb-4">
                    {/* Current Balance */}
                    <div className="text-center">
                      <div className="bg-gray-200 rounded px-3 py-2 min-w-[50px]">
                        <div className="text-sm font-bold text-gray-700">{item.current_stock}</div>
                      </div>
                      <div className="text-xs text-gray-500 mt-1">Current</div>
                    </div>

                    <div className="text-gray-400 text-lg">→</div>

                    {/* Movement Input */}
                    <div className="text-center">
                      <input
                        type="number"
                        value={adjustmentForm.quantity}
                        onChange={(e) => setAdjustmentForm(prev => ({ ...prev, quantity: parseInt(e.target.value) || 0 }))}
                        className={`w-16 px-2 py-2 text-sm font-bold text-center rounded border-2 focus:outline-none ${
                          adjustmentForm.quantity > 0
                            ? 'bg-green-50 border-green-300 text-green-600'
                            : adjustmentForm.quantity < 0
                              ? 'bg-red-50 border-red-300 text-red-600'
                              : 'bg-gray-50 border-gray-300 text-gray-600'
                        }`}
                        placeholder="+/-"
                      />
                      <div className="text-xs text-gray-500 mt-1">Movement</div>
                    </div>

                    <div className="text-gray-400 text-lg">→</div>

                    {/* New Balance Input */}
                    <div className="text-center">
                      <input
                        type="number"
                        min="0"
                        value={Math.max(0, item.current_stock + adjustmentForm.quantity)}
                        onChange={(e) => {
                          const newBalance = parseInt(e.target.value) || 0;
                          const movement = newBalance - item.current_stock;
                          setAdjustmentForm(prev => ({ ...prev, quantity: movement }));
                        }}
                        className="w-16 px-2 py-2 text-sm font-bold text-center bg-blue-50 border-2 border-blue-300 text-blue-600 rounded focus:outline-none"
                      />
                      <div className="text-xs text-gray-500 mt-1">New Balance</div>
                    </div>
                  </div>

                  {/* Remarks */}
                  <input
                    type="text"
                    value={adjustmentForm.remarks}
                    onChange={(e) => setAdjustmentForm(prev => ({ ...prev, remarks: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded text-sm mb-4"
                    placeholder="Remarks (optional)"
                  />

                  {/* Save Button */}
                  <button
                    onClick={handleAdjustment}
                    disabled={loading || adjustmentForm.quantity === 0}
                    className="w-full bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 text-white py-2 rounded font-medium flex items-center justify-center gap-2 transition-colors"
                  >
                    {loading ? <LoadingSpinner /> : <Save className="w-4 h-4" />}
                    Save Adjustment
                  </button>
                </div>
              ) : (
                <div className="text-center py-8">
                  <Settings className="w-8 h-8 text-gray-400 mx-auto mb-2" />
                  <h4 className="font-medium text-gray-900 mb-1">Admin Access Required</h4>
                  <p className="text-gray-500 text-sm">Stock adjustments require admin access.</p>
                </div>
              )}
            </div>
          )}

          {/* History Tab */}
          {activeTab === 'history' && (
            <div className="space-y-4">
              {loading ? (
                <div className="flex items-center justify-center py-8">
                  <LoadingSpinner />
                </div>
              ) : movementHistory.length === 0 ? (
                <div className="text-center py-12">
                  <History className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                  <h4 className="text-lg font-medium text-gray-900 mb-2">No Movement History</h4>
                  <p className="text-gray-500">No stock movements found for this item.</p>
                </div>
              ) : (
                <div className="max-h-[60vh] overflow-y-auto">
                  <div className="space-y-3 pr-2">
                    {movementHistory.map((movement: any, index: number) => (
                    <div key={movement.id} className="bg-white border border-gray-200 rounded-lg p-3 sm:p-4 hover:shadow-md transition-shadow">
                      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 sm:gap-0 mb-3">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium self-start ${
                          movement.movement_type === 'opening_stock'
                            ? 'bg-purple-100 text-purple-800'
                            : movement.movement_type === 'purchase_in' 
                              ? 'bg-green-100 text-green-800' 
                              : movement.movement_type === 'adjustment'
                                ? 'bg-blue-100 text-blue-800'
                                : movement.movement_type === 'delivery_out'
                                  ? 'bg-orange-100 text-orange-800'
                                  : 'bg-gray-100 text-gray-800'
                        }`}>
                          {movement.movement_type === 'opening_stock' ? 'OPENING' :
                           movement.movement_type === 'purchase_in' ? 'PURCHASE' :
                           movement.movement_type === 'delivery_out' ? 'DELIVERY' :
                           movement.movement_type === 'adjustment' ? 'ADJUSTMENT' :
                           movement.movement_type.replace('_', ' ').toUpperCase()}
                        </span>
                        <div className="text-sm text-gray-500">
                          <div className="sm:text-right">
                            {new Date(movement.movement_date).toLocaleDateString()}
                            {movement.time_only && (
                              <span className="block sm:inline sm:ml-2 text-blue-600 font-mono text-xs sm:text-sm">{movement.time_only}</span>
                            )}
                          </div>
                        </div>
                      </div>

                      <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 sm:gap-4 text-sm">
                        <div>
                          <span className="text-gray-500">Started:</span>
                          <div className="font-semibold text-gray-700">{movement.balance_before || 0}</div>
                        </div>
                        <div>
                          <span className="text-gray-500">Change:</span>
                          <div className={`font-semibold ${
                            (movement.quantity_change || movement.quantity) > 0 ? 'text-green-600' : 'text-red-600'
                          }`}>
                            {movement.quantity_change !== undefined 
                              ? (movement.quantity_change > 0 ? '+' : '') + movement.quantity_change
                              : (movement.quantity > 0 ? '+' : '') + movement.quantity
                            }
                          </div>
                        </div>
                        <div>
                          <span className="text-gray-500">Balance:</span>
                          <div className="font-semibold text-blue-600">
                            {movement.running_balance || movement.balance_after || 0}
                          </div>
                        </div>
                      </div>

                      {movement.remarks && (
                        <div className="mt-3 text-sm text-gray-600 bg-gray-50 rounded p-2">
                          {movement.remarks}
                        </div>
                      )}

                      <div className="mt-3 flex items-center justify-between text-xs text-gray-500">
                        <span className="font-mono">{movement.reference_number}</span>
                        <span>by {movement.created_by_phone || 'System'}</span>
                      </div>
                    </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between p-6 border-t border-gray-200 bg-gray-50">
          <div className="text-sm text-gray-600">
            Created: {new Date(item.created_at).toLocaleDateString()}
            {item.updated_at && item.updated_at !== item.created_at && (
              <span className="ml-4">
                Updated: {new Date(item.updated_at).toLocaleDateString()}
              </span>
            )}
          </div>
          <button
            onClick={onClose}
            className="btn-primary"
          >
            Close
          </button>
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-60 p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md">
            <div className="p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="bg-red-100 p-2 rounded-full">
                  <AlertTriangle className="w-5 h-5 text-red-600" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900">Confirm Deletion</h3>
              </div>
              
              <p className="text-gray-600 mb-6">
                Are you sure you want to remove <strong>{item.name}</strong>? This action cannot be undone.
              </p>

              <div className="flex justify-end gap-3">
                <button
                  onClick={() => setShowDeleteConfirm(false)}
                  className="btn-secondary"
                >
                  Cancel
                </button>
                <button
                  onClick={handleDeleteItem}
                  disabled={loading}
                  className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-lg flex items-center gap-2 transition-colors"
                >
                  {loading ? (
                    <LoadingSpinner />
                  ) : (
                    <Trash2 className="w-4 h-4" />
                  )}
                  Delete Item
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ItemDetailsModal;